import React from 'react';

import {
    Footer,
    Meta,
    RichText
} from '$partials/global/index.js';
import {
    VerifiedTick,
    ProBadge
} from '$partials/icons/index.js';
import { Image } from '$partials/media';
import BackgroundVideo from '$partials/media/background-video';
import ModuleDispatcher from '$partials/modules/dispatcher.jsx';
import ArticleCarousel from '$partials/modules/individual/article-carousel.jsx';
import { findModuleAnchors, getAllModules, getModuleSlug } from '$utilities/data';
import AnchorNavigation from '$partials/ui/anchor-navigation';
import { Button } from '$partials/ui';
import { resolvePath } from '$utilities/routes';
import { useLocale } from '$contexts/locale.jsx';


/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').ArticleDetailPage}
 */

const ArticleDetailPage = ({ data, domain }) => {

    const { translate: T } = useLocale();

    const page = data?.page;
    const listingPath = page ? resolvePath({ cfMetadata: /** @type {any} */ (page.cfMetadata) }) : null;
    const modules = getAllModules(page);
    /** @type {ReturnType<findModuleAnchors>} */
    let anchorModules = [];
    if (modules) {
        anchorModules = findModuleAnchors(modules);
    }

    if (!page) { return null; }
    return (
        <>
            <Meta page={page} />

            <main id='main-content'>
                <div className='min-h-screen'>
                    <section className='layout-container layout-site-outer-padding flex flex-col pb-24 pt-[116px] sm:pt-[108px] md:pt-[160px]'>
                        <h1 className='type-style-3-extended text-text-primary'>
                            {page?.pageTitle}
                        </h1>
                        {page?.avatar && (
                            page.avatar?.link ? (
                                <Button
                                    as='a'
                                    className='flex items-center gap-16 mt-24 group'
                                    href={page.avatar.link}>
                                    {page.avatar?.avatarImage && (
                                        <div className='relative'>
                                            <Image
                                                className='object-cover h-full w-full'
                                                containerClassName='rounded-full h-[56px] w-[56px] overflow-hidden relative'
                                                loading={page.avatar?.avatarImage?.loading}
                                                src={page.avatar?.avatarImage?.src}/>
                                            <div
                                                className='absolute inset-0 h-[56px] w-[56px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity'
                                                style={{ background: 'rgba(0, 0, 0, 0.15)' }} />
                                            {page.avatar?.verified && (
                                                <div className='absolute top-0 -right-5'>
                                                    <VerifiedTick />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className='flex flex-col gap-y-3'>
                                        <span className='type-style-body text-text-primary group-hover:text-brand-500 transition-colors'>
                                            {page.avatar?.name}
                                            {page.avatar?.pro && <ProBadge className='inline-block ml-8 w-24'/>}
                                        </span>
                                        <span className='type-style-body text-text-tertiary'>{page.avatar?.role}</span>
                                    </div>
                                </Button>
                            ) : (
                                <div className='flex items-center gap-16 mt-24'>
                                    {page.avatar?.avatarImage && (
                                        <div className='relative'>
                                            <Image
                                                className='object-cover h-full w-full'
                                                containerClassName='rounded-full h-[56px] w-[56px] overflow-hidden relative'
                                                loading={page.avatar?.avatarImage?.loading}
                                                src={page.avatar?.avatarImage?.src}/>
                                            {page.avatar?.verified && (
                                                <div className='absolute top-0 -right-5'>
                                                    <VerifiedTick />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className='flex flex-col gap-y-3'>
                                        <span className='type-style-body text-text-primary'>
                                            {page.avatar?.name}
                                            {page.avatar?.pro && <ProBadge className='inline-block ml-8 w-24'/>}
                                        </span>
                                        <span className='type-style-body text-text-tertiary'>{page.avatar?.role}</span>
                                    </div>
                                </div>
                            )
                        )}
                        {page?.heroMedia?.src && (
                            page.heroMedia?.contentType === 'video/mp4' ? (
                                <figure className='rounded overflow-hidden pointer-events-none select-none aspect-[15/8] h-full mt-24'>
                                    <BackgroundVideo
                                        className='rounded object-cover h-full w-full'
                                        containerClassName='w-full rounded h-full'
                                        src={page?.heroMedia?.src}/>
                                </figure>
                            ) : (

                                <figure className='rounded overflow-clip relative pointer-events-none select-none aspect-[15/8] h-full mt-24'>
                                    <Image
                                        className='h-full object-cover'
                                        containerClassName='h-full object-cover'
                                        height={'100%'}
                                        loading={page?.heroMedia?.loading}
                                        src={page?.heroMedia?.src}
                                        width={'100%'} />
                                </figure>

                            )
                        )}
                    </section>
                    {anchorModules.length > 0 && (
                        <AnchorNavigation anchors={anchorModules} />

                    )}

                    {page?.standfirst && (
                        <div className='layout-container layout-site-outer-padding py-24'>
                            <RichText
                                className='md:w-8/12 text-text-primary rich-text-article-standfirst'
                                {...page.standfirst} />
                        </div>

                    )}
                    {/* Content */}
                    {(page?.modules?.items && page?.modules?.items?.length > 0) && (
                        page.modules.items.map((module, i) => {
                            if (!module) { return null; }
                            const slug = getModuleSlug(module);
                            const nextTypename = page.modules?.items[i + 1]?.__typename ?? null;
                            const previousTypename = page.modules?.items[i - 1]?.__typename ?? null;
                            return (
                                <div
                                    id={slug || undefined}
                                    key={i}>
                                    {module.articleIncludeInAnchorNavigation && (
                                        <div className='layout-container layout-site-outer-padding pt-40'>
                                            <div className='border-t border-[var(--border-default)]' />
                                        </div>
                                    )}
                                    <ModuleDispatcher
                                        isArticlePage={true}
                                        isInAccordion={false}
                                        module={module}
                                        nextTypename={nextTypename}
                                        previousTypename={previousTypename}/>
                                </div>
                            );
                        })
                    )}
                    {page.relatedArticlesCollection?.items?.length > 0 && (
                        <ArticleCarousel
                            ctaLabel={T('View all')}
                            ctaLink={listingPath !== '/' ? listingPath : null}
                            heading={page?.relatedArticlesHeading}
                            isArticlePage={true}
                            itemsCollection={page.relatedArticlesCollection} />
                    )}
                    <div className='pt-8'>
                        <Footer
                            domain={domain}
                            menu={data?.footer} />
                    </div>
                </div>
            </main>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ArticleDetailPage;
